import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AutoComplete, Spin } from 'antd';
import { FiltersService } from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants/dropdown.constants';

interface SkillsAutoCompleteProps {
  value: string;
  onChange: (value: string, option?: {label: string, value: string}) => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onDropdownLoad?: (options: {label: string, value: string}[]) => void; // Callback when dropdown options are loaded
}

const SkillsAutoComplete: React.FC<SkillsAutoCompleteProps> = ({
  value,
  onChange,
  error,
  placeholder = 'Search Skills *',
  disabled = false,
  className = '',
  onDropdownLoad
}) => {
  const [options, setOptions] = useState<{label: string, value: string}[]>([]);
  const [loading, setLoading] = useState(false);
  const onDropdownLoadRef = useRef(onDropdownLoad);

  useEffect(() => {
    onDropdownLoadRef.current = onDropdownLoad;
  }, [onDropdownLoad]);

  const loadSkills = useCallback(async () => {
    setLoading(true);
    try {
      const skills = await FiltersService.getDropdownItems(DROPDOWN_CODES.Skill);
      const skillOptions = skills.map((skill: any) => ({
        label: skill.label || '',
        value: (skill.value || '').trim().toUpperCase()
      }));
      setOptions(skillOptions);
      onDropdownLoadRef.current?.(skillOptions);
    } catch (error) {
      console.error('Failed to load skills:', error);
      setOptions([]);
      onDropdownLoadRef.current?.([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  return (
    <AutoComplete
      className={className + (error ? ' is-invalid' : '') + ' skills-autocomplete-input'}
      placeholder={placeholder}
      value={value}
      options={options}
      disabled={disabled}
      filterOption={(inputValue, option) =>
        option && option.label && typeof option.label === 'string'
          ? option.label.toLowerCase().includes(inputValue.toLowerCase())
          : false
      }
      onSelect={(value, option) => onChange(value, option as {label: string, value: string})}
      onChange={(value, option) => onChange(value, option as {label: string, value: string})}
      notFoundContent={loading ? <Spin size="small" /> : null}
      allowClear
      style={{ width: '100%' }}
      size="large"
    />
  );
};

export default SkillsAutoComplete; 